"use client"

import { useMemo, useState, useTransition } from "react"
import { Controller, useForm, type Resolver } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Loader2 } from "lucide-react"
import { useTranslations } from "next-intl"

import { Icon } from "@/shared/components/icons"

import { useAuth } from "@/features/auth/hooks/use-auth"
import { submitSupportAction } from "@/features/support/actions"
import {
  SUPPORT_CATEGORIES,
  supportFormSchemaAuthenticated,
  supportFormSchemaGuest,
  type SupportCategory,
} from "@/features/support/validations"
import { track } from "@/shared/lib/analytics"
import { ROUTES } from "@/shared/lib/constants"
import { cn } from "@/shared/lib/utils"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card"
import { Button } from "@/shared/components/ui/button"
import { Input } from "@/shared/components/ui/input"
import { Label } from "@/shared/components/ui/label"
import { Textarea } from "@/shared/components/ui/textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select"
import { toast } from "sonner"

interface FormValues {
  category: SupportCategory
  subject: string
  message: string
  guestEmail?: string
  guestName?: string
}

export function SupportPageContent() {
  const t = useTranslations("support")
  const common = useTranslations("common")
  const errorsT = useTranslations("errors")
  const forms = useTranslations("forms")
  const { user, isAuthenticated } = useAuth()
  const [submitted, setSubmitted] = useState(false)
  const [pending, startTransition] = useTransition()

  const formMode = isAuthenticated ? "signed-in" : "guest"

  const resolver = useMemo(() => {
    return zodResolver(
      formMode === "signed-in"
        ? supportFormSchemaAuthenticated
        : supportFormSchemaGuest
    ) as Resolver<FormValues>
  }, [formMode])

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver,
    mode: "onSubmit",
    defaultValues: {
      category: "technical",
      subject: "",
      message: "",
      guestEmail: "",
      guestName: "",
    },
  })

  function onSubmit(values: FormValues) {
    if (isAuthenticated && !user) {
      toast.error(errorsT("sessionLoading"))
      return
    }

    startTransition(async () => {
      const result = await submitSupportAction({
        category: values.category,
        subject: values.subject,
        message: values.message,
        guestEmail: values.guestEmail,
        guestName: values.guestName,
      })

      track("support:form_submit", {
        category: values.category,
        authenticated: isAuthenticated,
      })

      if (!result.success) {
        toast.error(result.error ?? errorsT("generic"))
        return
      }

      setSubmitted(true)
      toast.success(t("toastSent"), {
        description: t("toastSentDescription"),
      })
    })
  }

  return (
    <div className="mx-auto max-w-2xl space-y-8">
      <div>
        <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-border/60 bg-muted/40 px-3 py-1 text-xs font-medium text-muted-foreground">
          <Icon name="qustion-circle" size={14} className="size-3.5 text-primary" aria-hidden />
          {t("eyebrow")}
        </div>
        <h1 className="text-2xl font-semibold tracking-tight text-foreground md:text-3xl">
          {t("title")}
        </h1>
        <p className="mt-2 max-w-xl text-sm text-muted-foreground md:text-base">
          {t("description")}
        </p>
      </div>

      <section className="space-y-5 text-sm leading-6 text-muted-foreground">
        <p>{t("intro")}</p>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="rounded-lg border border-border/60 bg-card/40 p-4">
            <h2 className="text-sm font-semibold text-foreground">
              {t("includeTitle")}
            </h2>
            <p className="mt-2">{t("includeBody")}</p>
          </div>
          <div className="rounded-lg border border-border/60 bg-card/40 p-4">
            <h2 className="text-sm font-semibold text-foreground">
              {t("responseTitle")}
            </h2>
            <p className="mt-2">{t("responseBody")}</p>
          </div>
        </div>
      </section>

      <Card className="border-border/60 bg-card/60">
        <CardHeader className="space-y-1 pb-4">
          <CardTitle className="text-lg">{t("formTitle")}</CardTitle>
          <CardDescription>{t("formDescription")}</CardDescription>
        </CardHeader>
        <CardContent>
          {submitted ? (
            <div className="space-y-4 py-2">
              <p className="text-sm text-foreground">
                {t("sentTitle")} {t("sentBody")}{" "}
                <a
                  className="text-primary underline-offset-4 hover:underline"
                  href="mailto:support@pleasur.ai"
                >
                  support@pleasur.ai
                </a>
                .
              </p>
              <Button type="button" variant="outline" onClick={() => setSubmitted(false)}>
                {t("sendAnother")}
              </Button>
            </div>
          ) : (
            <form
              key={formMode}
              onSubmit={handleSubmit(onSubmit)}
              className="space-y-6"
            >
              {!isAuthenticated && (
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="guest-email">{common("email")}</Label>
                    <Input
                      id="guest-email"
                      autoComplete="email"
                      placeholder={forms("emailPlaceholder")}
                      className="bg-background/50 border-border/50 focus:border-white/40"
                      {...register("guestEmail")}
                    />
                    {errors.guestEmail ? (
                      <p className="text-xs text-destructive">{errors.guestEmail.message}</p>
                    ) : (
                      <p className="text-xs text-muted-foreground">
                        {t("guestRequired")}{" "}
                        <a href={ROUTES.login} className="text-primary underline-offset-4 hover:underline">
                          {t("signIn")}
                        </a>
                        .
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="guest-name">{t("nameOptional")}</Label>
                    <Input
                      id="guest-name"
                      autoComplete="name"
                      placeholder={forms("namePlaceholder")}
                      className="bg-background/50 border-border/50 focus:border-white/40"
                      {...register("guestName")}
                    />
                    {errors.guestName ? (
                      <p className="text-xs text-destructive">{errors.guestName.message}</p>
                    ) : null}
                  </div>
                </div>
              )}

              <Controller
                name="category"
                control={control}
                render={({ field }) => (
                  <div className="space-y-2">
                    <Label htmlFor="support-category">{t("category")}</Label>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger
                        id="support-category"
                        className={cn(
                          "bg-background/50 border-border/50 focus:border-white/40",
                          errors.category && "border-destructive"
                        )}
                      >
                        <SelectValue placeholder={t("chooseCategory")} />
                      </SelectTrigger>
                      <SelectContent>
                        {SUPPORT_CATEGORIES.map((category) => (
                          <SelectItem key={category.value} value={category.value}>
                            {t(`categories.${category.value}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.category ? (
                      <p className="text-xs text-destructive">{errors.category.message}</p>
                    ) : null}
                  </div>
                )}
              />

              <div className="space-y-2">
                <Label htmlFor="support-subject">{t("subject")}</Label>
                <Input
                  id="support-subject"
                  placeholder={forms("subjectPlaceholder")}
                  className="bg-background/50 border-border/50 focus:border-white/40"
                  {...register("subject")}
                />
                {errors.subject ? (
                  <p className="text-xs text-destructive">{errors.subject.message}</p>
                ) : null}
              </div>

              <div className="space-y-2">
                <Label htmlFor="support-message">{t("message")}</Label>
                <Textarea
                  id="support-message"
                  rows={6}
                  placeholder={t("messagePlaceholder")}
                  className="min-h-[140px] resize-y bg-background/50 border-border/50 focus:border-white/40"
                  {...register("message")}
                />
                {errors.message ? (
                  <p className="text-xs text-destructive">{errors.message.message}</p>
                ) : null}
              </div>

              <Button
                type="submit"
                disabled={pending}
                className="bg-primary text-primary-foreground hover:bg-primary/90"
              >
                {pending ? (
                  <>
                    <Loader2 className="mr-2 size-4 animate-spin" aria-hidden />
                    {t("sending")}
                  </>
                ) : (
                  t("sendMessage")
                )}
              </Button>

              <p className="text-xs text-muted-foreground">{t("delivered")}</p>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
